import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  CircularProgress,
  Chip,
  Fade,
  Slide,
  Checkbox,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Upload as UploadIcon,
  DragIndicator as DragIcon,
  Crop as CropIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  DeleteSweep as DeleteSweepIcon,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import { getClasses } from "../services/api";
import axios from "axios";
import SEOHead from "../components/SEOHead";
import CitabilitySection from "../components/CitabilitySection";
import { Link } from "react-router-dom";
import ImageCropDialog from "../components/ImageCropDialog";
import EnhancedGalleryManager from "../components/EnhancedGalleryManager";
import { API_URL } from "../config";
import { clearAllCaches, forceHardReload } from "../utils/cacheBuster";

interface ClassDetails {
  duration: string;
  materials: string[];
  price: number;
  schedule: string;
  location: string;
}

interface Class {
  _id: string;
  name: string;
  description: string;
  type: string;
  mainImage: string;
  thumbnailImage?: string; // Optional: for listing page
  gallery: {
    showcase: string[];
    studentWork: string[];
  };
  details: ClassDetails;
}

const Classes: React.FC = () => {
  const { user } = useAuth();
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [currentClass, setCurrentClass] = useState<Class | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [imageUploading, setImageUploading] = useState({
    main: false,
    showcase: false,
    studentWork: false,
  });
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [tempImageForCrop, setTempImageForCrop] = useState<string>("");
  const [cropImageType, setCropImageType] = useState<"main" | "thumbnail" | "gallery">("main");
  const [cropGalleryType, setCropGalleryType] = useState<"showcase" | "studentWork">("showcase");
  const [cropGalleryIndex, setCropGalleryIndex] = useState<number>(0);
  const [selectedShowcaseImages, setSelectedShowcaseImages] = useState<number[]>([]);
  const [selectedStudentWorkImages, setSelectedStudentWorkImages] = useState<number[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [newClass, setNewClass] = useState<Class>({
    _id: "",
    name: "",
    description: "",
    type: "",
    mainImage: "",
    thumbnailImage: "",
    gallery: {
      showcase: [],
      studentWork: [],
    },
    details: {
      duration: "",
      materials: [],
      price: 0,
      schedule: "",
      location: "",
    },
  });

  useEffect(() => {
    fetchClasses();

    // Set up SMART auto-refresh - pauses during user activity
    const intervalId = setInterval(() => {
      // Don't refresh if user is actively editing or uploading
      if (editDialogOpen || submitting || imageUploading) {
        return;
      }

      fetchClasses();
    }, 30000);

    return () => {
      clearInterval(intervalId);
    };
  }, [editDialogOpen, submitting, imageUploading]);

  // Force refresh data when component becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchClasses();
      }
    };

    const handleFocus = () => {
      fetchClasses();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  // Reset currentClass when dialog closes
  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setCurrentClass(null);
  };

  // Check if required text fields are filled before allowing image upload
  const areRequiredFieldsFilled = (): boolean => {
    const classData = currentClass || newClass;
    return !!(
      classData.name?.trim() &&
      classData.description?.trim() &&
      classData.type?.trim() &&
      classData.details?.duration?.trim() &&
      classData.details?.schedule?.trim() &&
      classData.details?.location?.trim() &&
      (classData.details?.price ?? 0) > 0
    );
  };

  const fetchClasses = async () => {
    try {
      const data = await getClasses();
      setClasses(data);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      setError(`Failed to load classes: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const refreshSingleClass = async (classId: string) => {
    try {
      const data = await getClasses();
      const updatedClass = data.find((c) => c._id === classId);
      if (updatedClass) {
        setClasses(data); // Update the entire list
        setCurrentClass(ensureClassDefaults(updatedClass)); // Update current class with fresh data
      }
    } catch (err) {
      // Error handling
    }
  };

  // Force update all data (aggressive refresh)
  const forceUpdateAllData = async () => {
    try {
      setLoading(true);

      // Clear any cached state
      setClasses([]);
      setCurrentClass(null);

      // Wait a bit to ensure state is cleared
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Fetch fresh data
      await fetchClasses();
    } catch (err) {
      setError("Failed to refresh data");
    }
  };

  // Helper to ensure class has all required fields with defaults
  const ensureClassDefaults = (classItem: Class): Class => ({
    ...classItem,
    details: {
      duration: classItem.details?.duration || "",
      materials: classItem.details?.materials || [],
      price: classItem.details?.price || 0,
      schedule: classItem.details?.schedule || "",
      location: classItem.details?.location || "",
    },
    gallery: {
      showcase: classItem.gallery?.showcase || [],
      studentWork: classItem.gallery?.studentWork || [],
    },
  });

  const handleEditClick = (classItem: Class) => {
    // Find the latest data for this class from the classes array
    const latestClassData = classes.find((c) => c._id === classItem._id);
    const classToEdit = latestClassData || classItem;
    
    // Ensure all fields have defaults to prevent undefined errors
    setCurrentClass(ensureClassDefaults(classToEdit));

    // Use setTimeout to ensure state is set before opening dialog
    setTimeout(() => {
      setEditDialogOpen(true);
    }, 0);
  };

  const handleDeleteClick = async (classId: string) => {
    if (!user) return;

    try {
      await axios.delete(`${API_URL}/api/classes/${classId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      fetchClasses();
    } catch (err) {
      setError("Failed to delete class");
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !currentClass || submitting) return;

    setSubmitting(true);
    try {
      const formData = new FormData();
      const mainImageFile = (
        document.getElementById("main-image-upload") as HTMLInputElement
      ).files?.[0];
      const showcaseFiles = (
        document.getElementById("showcase-upload") as HTMLInputElement
      ).files;
      const studentWorkFiles = (
        document.getElementById("student-work-upload") as HTMLInputElement
      ).files;

      if (mainImageFile) {
        formData.append("mainImage", mainImageFile);
      }

      if (showcaseFiles) {
        Array.from(showcaseFiles).forEach((file) => {
          formData.append("showcase", file);
        });
      }

      if (studentWorkFiles) {
        Array.from(studentWorkFiles).forEach((file) => {
          formData.append("studentWork", file);
        });
      }

      formData.append("name", currentClass.name);
      formData.append("description", currentClass.description);
      formData.append("type", currentClass.type);
      formData.append("details", JSON.stringify(currentClass.details));

      await axios.put(`${API_URL}/api/classes/${currentClass._id}`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
        },
      });
      // Force complete refresh to ensure fresh data
      await forceUpdateAllData();

      // Close dialog and let user see the updated main page
      handleDialogClose();
    } catch (err) {
      setError("Failed to update class");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      setSubmitting(true);
      const formData = new FormData();
      const mainImageFile = (
        document.getElementById("main-image-upload") as HTMLInputElement
      ).files?.[0];
      const showcaseFiles = (
        document.getElementById("showcase-upload") as HTMLInputElement
      ).files;
      const studentWorkFiles = (
        document.getElementById("student-work-upload") as HTMLInputElement
      ).files;

      if (mainImageFile) {
        formData.append("mainImage", mainImageFile);
      }

      if (showcaseFiles) {
        Array.from(showcaseFiles).forEach((file) => {
          formData.append("showcase", file);
        });
      }

      if (studentWorkFiles) {
        Array.from(studentWorkFiles).forEach((file) => {
          formData.append("studentWork", file);
        });
      }

      formData.append("name", newClass.name);
      formData.append("description", newClass.description);
      formData.append("type", newClass.type);
      formData.append("details", JSON.stringify(newClass.details));

      await axios.post(`${API_URL}/api/classes`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setEditDialogOpen(false);
      fetchClasses();
    } catch (err) {
      setError("Failed to create class");
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageUpload = (
    files: FileList | null,
    type: "showcase" | "studentWork"
  ) => {
    if (!files) return;

    setImageUploading((prev) => ({ ...prev, [type]: true }));

    let processedCount = 0;
    const totalFiles = files.length;
    const newImages: string[] = [];

    const updateGallery = (images: string[]) => {
      if (currentClass) {
        // Edit mode - update currentClass
        setCurrentClass((prevClass) => {
          if (!prevClass) return prevClass;
          const existingImages = prevClass.gallery[type] || [];
          return {
            ...prevClass,
            gallery: {
              ...prevClass.gallery,
              [type]: [...existingImages, ...images],
            },
          };
        });
      } else {
        // Add mode - update newClass
        setNewClass((prevClass) => {
          const existingImages = prevClass.gallery[type] || [];
          return {
            ...prevClass,
            gallery: {
              ...prevClass.gallery,
              [type]: [...existingImages, ...images],
            },
          };
        });
      }
      setImageUploading((prev) => ({ ...prev, [type]: false }));
    };

    Array.from(files).forEach((file) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        processedCount++;
        newImages.push(reader.result as string);

        if (processedCount === totalFiles) {
          updateGallery(newImages);
        }
      };

      reader.onerror = () => {
        processedCount++;
        if (processedCount === totalFiles) {
          updateGallery(newImages);
        }
      };

      reader.readAsDataURL(file);
    });
  };

  const handleImageDelete = (
    index: number,
    type: "showcase" | "studentWork"
  ) => {
    if (currentClass) {
      // Edit mode
      setCurrentClass((prevClass) => {
        if (!prevClass) return prevClass;
        const updatedImages = [...prevClass.gallery[type]];
        updatedImages.splice(index, 1);
        return {
          ...prevClass,
          gallery: {
            ...prevClass.gallery,
            [type]: updatedImages,
          },
        };
      });
    } else {
      // Add mode
      setNewClass((prevClass) => {
        const updatedImages = [...prevClass.gallery[type]];
        updatedImages.splice(index, 1);
        return {
          ...prevClass,
          gallery: {
            ...prevClass.gallery,
            [type]: updatedImages,
          },
        };
      });
    }
  };

  // Handle cropped image from crop dialog
  const handleCroppedImage = (croppedImage: string) => {
    if (cropImageType === "gallery") {
      // Cropping gallery image
      const galleryType = cropGalleryType;
      if (currentClass) {
        setCurrentClass((prevClass) => {
          if (!prevClass) return prevClass;
          const updatedImages = [...prevClass.gallery[galleryType]];
          updatedImages[cropGalleryIndex] = croppedImage;
          return {
            ...prevClass,
            gallery: {
              ...prevClass.gallery,
              [galleryType]: updatedImages,
            },
          };
        });
      } else {
        setNewClass((prev) => {
          const updatedImages = [...prev.gallery[galleryType]];
          updatedImages[cropGalleryIndex] = croppedImage;
          return {
            ...prev,
            gallery: {
              ...prev.gallery,
              [galleryType]: updatedImages,
            },
          };
        });
      }
    } else {
      // Cropping main or thumbnail image
      const imageField = cropImageType === "main" ? "mainImage" : "thumbnailImage";
      
      if (currentClass) {
        // Edit mode
        setCurrentClass({
          ...currentClass,
          [imageField]: croppedImage,
        });
      } else {
        // Add mode
        setNewClass((prev) => ({
          ...prev,
          [imageField]: croppedImage,
        }));
      }
    }
    setCropDialogOpen(false);
    setTempImageForCrop("");
  };

  // Handle crop gallery image
  const handleCropGalleryImage = (
    index: number,
    type: "showcase" | "studentWork"
  ) => {
    const imageUrl = currentClass
      ? currentClass.gallery[type][index]
      : newClass.gallery[type][index];
    
    setCropImageType("gallery");
    setCropGalleryType(type);
    setCropGalleryIndex(index);
    setTempImageForCrop(imageUrl);
    setCropDialogOpen(true);
  };

  // Handle bulk delete
  const handleBulkDelete = (type: "showcase" | "studentWork") => {
    const selectedImages = type === "showcase" ? selectedShowcaseImages : selectedStudentWorkImages;
    
    if (selectedImages.length === 0) return;

    if (currentClass) {
      setCurrentClass((prevClass) => {
        if (!prevClass) return prevClass;
        const updatedImages = prevClass.gallery[type].filter(
          (_, index) => !selectedImages.includes(index)
        );
        return {
          ...prevClass,
          gallery: {
            ...prevClass.gallery,
            [type]: updatedImages,
          },
        };
      });
    } else {
      setNewClass((prev) => {
        const updatedImages = prev.gallery[type].filter(
          (_, index) => !selectedImages.includes(index)
        );
        return {
          ...prev,
          gallery: {
            ...prev.gallery,
            [type]: updatedImages,
          },
        };
      });
    }

    // Clear selection
    if (type === "showcase") {
      setSelectedShowcaseImages([]);
    } else {
      setSelectedStudentWorkImages([]);
    }
  };

  // Handle image reorder
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (
    targetIndex: number,
    type: "showcase" | "studentWork"
  ) => {
    if (draggedIndex === null || draggedIndex === targetIndex) return;

    if (currentClass) {
      setCurrentClass((prevClass) => {
        if (!prevClass) return prevClass;
        const updatedImages = [...prevClass.gallery[type]];
        const [draggedItem] = updatedImages.splice(draggedIndex, 1);
        updatedImages.splice(targetIndex, 0, draggedItem);
        return {
          ...prevClass,
          gallery: {
            ...prevClass.gallery,
            [type]: updatedImages,
          },
        };
      });
    } else {
      setNewClass((prev) => {
        const updatedImages = [...prev.gallery[type]];
        const [draggedItem] = updatedImages.splice(draggedIndex, 1);
        updatedImages.splice(targetIndex, 0, draggedItem);
        return {
          ...prev,
          gallery: {
            ...prev.gallery,
            [type]: updatedImages,
          },
        };
      });
    }

    setDraggedIndex(null);
  };

  // Toggle selection
  const toggleImageSelection = (
    index: number,
    type: "showcase" | "studentWork"
  ) => {
    if (type === "showcase") {
      setSelectedShowcaseImages((prev) =>
        prev.includes(index)
          ? prev.filter((i) => i !== index)
          : [...prev, index]
      );
    } else {
      setSelectedStudentWorkImages((prev) =>
        prev.includes(index)
          ? prev.filter((i) => i !== index)
          : [...prev, index]
      );
    }
  };

  // Select all
  const toggleSelectAll = (type: "showcase" | "studentWork") => {
    const images = currentClass
      ? currentClass.gallery[type]
      : newClass.gallery[type];
    
    const selected = type === "showcase" ? selectedShowcaseImages : selectedStudentWorkImages;
    
    if (selected.length === images.length) {
      // Deselect all
      if (type === "showcase") {
        setSelectedShowcaseImages([]);
      } else {
        setSelectedStudentWorkImages([]);
      }
    } else {
      // Select all
      const allIndices = images.map((_, index) => index);
      if (type === "showcase") {
        setSelectedShowcaseImages(allIndices);
      } else {
        setSelectedStudentWorkImages(allIndices);
      }
    }
  };

  // Validate image URL
  const isValidImageUrl = (url: string): boolean => {
    return !!(url && (url.startsWith("http") || url.startsWith("data:")));
  };

  // Structured data for classes listing page - MUST be before early returns
  const classesStructuredData = useMemo((): any[] => {
    const baseUrl = "https://cosylabdesigns.com";
    return [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "@id": `${baseUrl}/classes#webpage`,
        name: "課程列表 - CosyLab",
        description:
          "瀏覽CosyLab的手工藝課程：蠟燭製作、植物護理等多元選擇。適合中小學生及成人參與的專業手工藝課程。",
        url: `${baseUrl}/classes`,
        mainEntity: {
          "@type": "ItemList",
          numberOfItems: classes.length,
          itemListElement: classes.map((classItem, index) => ({
            "@type": "ListItem",
            position: index + 1,
            item: {
              "@type": "Course",
              "@id": `${baseUrl}/classes/${classItem._id}`,
              name: classItem.name,
              description: classItem.description,
              image: classItem.mainImage,
              provider: {
                "@type": "EducationalOrganization",
                name: "CosyLab",
              },
              courseCode: classItem._id,
              educationalLevel: "Beginner to Advanced",
              teaches: classItem.type === "candle" ? "蠟燭製作" : "植物護理",
              inLanguage: "zh-Hant",
              duration: classItem.details.duration,
            },
          })),
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首頁",
            item: baseUrl,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "課程列表",
            item: `${baseUrl}/classes`,
          },
        ],
      },
    ];
  }, [classes]);

  // Early returns - must be after all hooks
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4} textAlign="center">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  const candleClasses = classes.filter((c) => c.type === "candle");
  const plantClasses = classes.filter((c) => c.type === "plant");

  return (
    <Box sx={{ 
      minHeight: "100vh",
      background: `
        radial-gradient(ellipse at top left, rgba(139, 195, 74, 0.08) 0%, transparent 50%),
        radial-gradient(ellipse at bottom right, rgba(104, 159, 56, 0.06) 0%, transparent 50%),
        linear-gradient(180deg, rgba(250, 251, 248, 1) 0%, rgba(245, 248, 242, 1) 100%)
      `,
      position: "relative",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(139, 195, 74, 0.02) 35px, rgba(139, 195, 74, 0.02) 70px),
          repeating-linear-gradient(-45deg, transparent, transparent 35px, rgba(104, 159, 56, 0.02) 35px, rgba(104, 159, 56, 0.02) 70px)
        `,
        pointerEvents: "none",
        zIndex: 0,
      }
    }}>
      <Container maxWidth="lg" sx={{ 
        py: 4,
        position: "relative",
        zIndex: 1,
      }}>
        <SEOHead
          title="課程列表 - CosyLab | 香港手工藝課程、興趣班、工作坊"
          description="瀏覽CosyLab的手工藝課程：蠟燭製作、植物護理等多元選擇。適合中小學生及成人參與的專業手工藝課程和工作坊。立即查看課程詳情並報名參加。"
          canonical="https://cosylabdesigns.com/classes"
          keywords="香港手工藝課程, 課程列表, 蠟燭製作課程, 植物護理課程, 香港興趣班列表, 工作坊列表, 手工藝班香港, 課程報名"
          structuredData={classesStructuredData}
        />
        <Slide direction="down" in={true} timeout={800}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 600,
              textAlign: "center",
              mb: 4,
              background: "linear-gradient(135deg, #2D5A27 0%, #8BC34A 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textShadow: "0 2px 4px rgba(139, 195, 74, 0.1)",
            }}
          >
            我們的課程
          </Typography>
        </Slide>

      {user && user.isOwner && (
        <Fade in={true} timeout={1000}>
          <Box
            mb={4}
            textAlign="center"
            sx={{ display: "flex", gap: 2, justifyContent: "center" }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setCurrentClass(null);
                setNewClass({
                  _id: "",
                  name: "",
                  description: "",
                  type: "",
                  mainImage: "",
                  gallery: {
                    showcase: [],
                    studentWork: [],
                  },
                  details: {
                    duration: "",
                    materials: [],
                    price: 0,
                    schedule: "",
                    location: "",
                  },
                });
                setEditDialogOpen(true);
              }}
              sx={{
                background: "linear-gradient(135deg, #2D5A27 0%, #8BC34A 100%)",
                color: "white",
                fontWeight: 500,
                boxShadow: "0 4px 12px rgba(139, 195, 74, 0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #689F38 0%, #8BC34A 100%)",
                  boxShadow: "0 6px 20px rgba(139, 195, 74, 0.4)",
                },
              }}
            >
              新增課程
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                forceUpdateAllData();
              }}
              sx={{
                color: "#2D5A27",
                borderColor: "#2D5A27",
                fontWeight: 500,
                "&:hover": {
                  backgroundColor: "rgba(139, 195, 74, 0.08)",
                  borderColor: "#689F38",
                },
              }}
            >
              刷新資料
            </Button>

          {/* Auto-refresh status indicator */}
          {(editDialogOpen || submitting || imageUploading) && (
            <Chip
              label="自動刷新已暫停"
              color="warning"
              size="small"
              sx={{ mr: 2 }}
            />
          )}

          <Button
            variant="outlined"
            color="warning"
            onClick={async () => {
              await clearAllCaches();
              forceHardReload();
            }}
            sx={{
              fontWeight: 500,
            }}
          >
            清除緩存
          </Button>
          </Box>
        </Fade>
      )}

      <Stack spacing={4}>
        <Fade in={true} timeout={1200}>
          <Box>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: 600,
                mb: 3,
                background: "linear-gradient(135deg, #2D5A27 0%, #8BC34A 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              蠟燭製作課程
            </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: 3,
            }}
          >
            {candleClasses.map((classItem) => (
              <Box key={classItem._id}>
                <Card
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    boxShadow: "0 8px 24px rgba(139, 195, 74, 0.12)",
                    transition: "all 0.3s ease-in-out",
                    height: "100%",
                    background: `
                      linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 251, 248, 0.95) 100%)
                    `,
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(139, 195, 74, 0.1)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 16px 40px rgba(139, 195, 74, 0.2)",
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Typography
                        variant="h6"
                        component={Link}
                        to={`/classes/${classItem._id}`}
                        sx={{
                          flexGrow: 1,
                          textDecoration: "none",
                          background: "linear-gradient(135deg, #2D5A27 0%, #8BC34A 100%)",
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          fontWeight: 600,
                          fontSize: { xs: "1.1rem", md: "1.3rem" },
                          "&:hover": {
                            background: "linear-gradient(135deg, #689F38 0%, #8BC34A 100%)",
                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                          },
                        }}
                      >
                        {classItem.name}
                      </Typography>
                      {user && user.isOwner && (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton
                            onClick={() => handleEditClick(classItem)}
                            size="small"
                            sx={{
                              color: "#2D5A27",
                              "&:hover": {
                                backgroundColor: "rgba(139, 195, 74, 0.08)",
                              },
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteClick(classItem._id)}
                            size="small"
                            sx={{
                              color: "#d32f2f",
                              "&:hover": {
                                backgroundColor: "rgba(211, 47, 47, 0.08)",
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                    <Link
                      to={`/classes/${classItem._id}`}
                      title={`${classItem.name} 詳情`}
                      style={{ display: "block", textDecoration: "none" }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: { xs: 180, sm: 200, md: 220 },
                          borderRadius: 1,
                          overflow: "hidden",
                          mb: 2,
                          transition: "transform 0.3s ease",
                          "&:hover": {
                            transform: "scale(1.02)",
                          },
                        }}
                      >
                        <img
                          src={classItem.thumbnailImage || classItem.mainImage}
                          alt={classItem.name}
                          loading="lazy"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>
                    </Link>
                    <Typography
                      variant="body2"
                      paragraph
                      sx={{
                        color: "#666666",
                        lineHeight: 1.5,
                        mb: 2,
                        whiteSpace: "pre-line",
                      }}
                    >
                      {classItem.description}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#666666",
                          mb: 0.5,
                        }}
                      >
                        時長: {classItem.details.duration}
                      </Typography>
                      {/* Schedule / Location / Materials hidden per new requirements */}
                    </Box>
                    <Button
                      component={Link}
                      to={`/classes/${classItem._id}`}
                      title={`${classItem.name} 課程詳情`}
                      variant="outlined"
                      fullWidth
                      sx={{
                        borderColor: "#2D5A27",
                        color: "#2D5A27",
                        fontWeight: 500,
                        "&:hover": {
                          background: "linear-gradient(135deg, #2D5A27 0%, #8BC34A 100%)",
                          color: "white",
                          borderColor: "#2D5A27",
                        },
                      }}
                    >
                      查看詳情
                    </Button>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        </Box>
        </Fade>

        <Fade in={true} timeout={1400}>
          <Box>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: 600,
                mb: 3,
                background: "linear-gradient(135deg, #2D5A27 0%, #8BC34A 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              植物護理課程
            </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: 3,
            }}
          >
            {plantClasses.map((classItem) => (
              <Box key={classItem._id}>
                <Card
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    boxShadow: "0 8px 24px rgba(139, 195, 74, 0.12)",
                    transition: "all 0.3s ease-in-out",
                    height: "100%",
                    background: `
                      linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(250, 251, 248, 0.95) 100%)
                    `,
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(139, 195, 74, 0.1)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 16px 40px rgba(139, 195, 74, 0.2)",
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Typography
                        variant="h6"
                        component={Link}
                        to={`/classes/${classItem._id}`}
                        sx={{
                          flexGrow: 1,
                          textDecoration: "none",
                          background: "linear-gradient(135deg, #2D5A27 0%, #8BC34A 100%)",
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          fontWeight: 600,
                          fontSize: { xs: "1.1rem", md: "1.3rem" },
                          "&:hover": {
                            background: "linear-gradient(135deg, #689F38 0%, #8BC34A 100%)",
                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                          },
                        }}
                      >
                        {classItem.name}
                      </Typography>
                      {user && user.isOwner && (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <IconButton
                            onClick={() => handleEditClick(classItem)}
                            size="small"
                            sx={{
                              color: "#2D5A27",
                              "&:hover": {
                                backgroundColor: "rgba(139, 195, 74, 0.08)",
                              },
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteClick(classItem._id)}
                            size="small"
                            sx={{
                              color: "#d32f2f",
                              "&:hover": {
                                backgroundColor: "rgba(211, 47, 47, 0.08)",
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                    <Link
                      to={`/classes/${classItem._id}`}
                      title={`${classItem.name} 詳情`}
                      style={{ display: "block", textDecoration: "none" }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: { xs: 180, sm: 200, md: 220 },
                          borderRadius: 1,
                          overflow: "hidden",
                          mb: 2,
                          transition: "transform 0.3s ease",
                          "&:hover": {
                            transform: "scale(1.02)",
                          },
                        }}
                      >
                        <img
                          src={classItem.thumbnailImage || classItem.mainImage}
                          alt={classItem.name}
                          loading="lazy"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>
                    </Link>
                    <Typography
                      variant="body2"
                      paragraph
                      sx={{
                        color: "#666666",
                        lineHeight: 1.5,
                        mb: 2,
                        whiteSpace: "pre-line",
                      }}
                    >
                      {classItem.description}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#666666",
                          mb: 0.5,
                        }}
                      >
                        時長: {classItem.details.duration}
                      </Typography>
                      {/* Schedule / Location / Materials hidden per new requirements */}
                    </Box>
                    <Button
                      component={Link}
                      to={`/classes/${classItem._id}`}
                      title={`${classItem.name} 課程詳情`}
                      variant="outlined"
                      fullWidth
                      sx={{
                        borderColor: "#2D5A27",
                        color: "#2D5A27",
                        fontWeight: 500,
                        "&:hover": {
                          background: "linear-gradient(135deg, #2D5A27 0%, #8BC34A 100%)",
                          color: "white",
                          borderColor: "#2D5A27",
                        },
                      }}
                    >
                      查看詳情
                    </Button>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        </Box>
        </Fade>
      </Stack>


      <Dialog
        open={editDialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {currentClass ? "Edit Class" : "Add New Class"}
        </DialogTitle>
        {/* Only render form content when dialog is open */}
        {editDialogOpen && (
          <form onSubmit={currentClass ? handleEditSubmit : handleAddSubmit}>
            <DialogContent>
              <TextField
                fullWidth
                label="Name"
                value={currentClass ? currentClass.name : newClass.name}
                onChange={(e) => {
                  if (currentClass) {
                    setCurrentClass({ ...currentClass, name: e.target.value });
                  } else {
                    setNewClass({ ...newClass, name: e.target.value });
                  }
                }}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Description"
                value={
                  currentClass ? currentClass.description : newClass.description
                }
                onChange={(e) => {
                  if (currentClass) {
                    setCurrentClass({
                      ...currentClass,
                      description: e.target.value,
                    });
                  } else {
                    setNewClass({
                      ...newClass,
                      description: e.target.value,
                    });
                  }
                }}
                margin="normal"
                multiline
                rows={4}
                required
                onKeyDown={(e) => {
                  if (e.key === "Enter" && e.shiftKey) {
                    // Allow new line with Shift+Enter
                    return;
                  }
                  if (e.key === "Enter") {
                    // Prevent form submission on Enter, allow new line
                    e.preventDefault();
                    const target = e.target as HTMLTextAreaElement;
                    const start = target.selectionStart;
                    const end = target.selectionEnd;
                    const value = target.value;
                    const newValue =
                      value.substring(0, start) + "\n" + value.substring(end);
                    target.value = newValue;
                    target.selectionStart = target.selectionEnd = start + 1;

                    // Update the state
                    setCurrentClass({
                      ...currentClass!,
                      description: newValue,
                    });
                  }
                }}
                helperText="Press Enter for new line, Shift+Enter for new line"
              />
              <TextField
                fullWidth
                select
                label="Type"
                value={currentClass ? currentClass.type : newClass.type}
                onChange={(e) => {
                  if (currentClass) {
                    setCurrentClass({
                      ...currentClass,
                      type: e.target.value as "candle" | "plant",
                    });
                  } else {
                    setNewClass({
                      ...newClass,
                      type: e.target.value as "candle" | "plant",
                    });
                  }
                }}
                margin="normal"
                required
                SelectProps={{
                  native: true,
                }}
              >
                <option value="">-- Select Type --</option>
                <option value="candle">Candle Making</option>
                <option value="plant">Plant Care</option>
              </TextField>
              <TextField
                fullWidth
                label="Duration"
                value={
                  currentClass
                    ? currentClass.details?.duration || ""
                    : newClass.details?.duration || ""
                }
                onChange={(e) => {
                  if (currentClass) {
                    setCurrentClass({
                      ...currentClass,
                      details: {
                        ...currentClass.details,
                        duration: e.target.value,
                      },
                    });
                  } else {
                    setNewClass({
                      ...newClass,
                      details: {
                        ...newClass.details,
                        duration: e.target.value,
                      },
                    });
                  }
                }}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Price"
                type="number"
                value={
                  currentClass
                    ? currentClass.details?.price || ""
                    : newClass.details?.price || ""
                }
                onChange={(e) => {
                  if (currentClass) {
                    setCurrentClass({
                      ...currentClass,
                      details: {
                        ...currentClass.details,
                        price: Number(e.target.value) || 0,
                      },
                    });
                  } else {
                    setNewClass({
                      ...newClass,
                      details: {
                        ...newClass.details,
                        price: Number(e.target.value) || 0,
                      },
                    });
                  }
                }}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Schedule"
                value={
                  currentClass
                    ? currentClass.details?.schedule || ""
                    : newClass.details?.schedule || ""
                }
                onChange={(e) => {
                  if (currentClass) {
                    setCurrentClass({
                      ...currentClass,
                      details: {
                        ...currentClass.details,
                        schedule: e.target.value,
                      },
                    });
                  } else {
                    setNewClass({
                      ...newClass,
                      details: {
                        ...newClass.details,
                        schedule: e.target.value,
                      },
                    });
                  }
                }}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Location"
                value={
                  currentClass
                    ? currentClass.details?.location || ""
                    : newClass.details?.location || ""
                }
                onChange={(e) => {
                  if (currentClass) {
                    setCurrentClass({
                      ...currentClass,
                      details: {
                        ...currentClass.details,
                        location: e.target.value,
                      },
                    });
                  } else {
                    setNewClass({
                      ...newClass,
                      details: {
                        ...newClass.details,
                        location: e.target.value,
                      },
                    });
                  }
                }}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Materials (comma-separated)"
                value={
                  currentClass
                    ? currentClass.details?.materials?.join(", ") || ""
                    : newClass.details?.materials?.join(", ") || ""
                }
                onChange={(e) => {
                  if (currentClass) {
                    setCurrentClass({
                      ...currentClass,
                      details: {
                        ...currentClass.details,
                        materials: e.target.value
                          .split(",")
                          .map((m) => m.trim()),
                      },
                    });
                  } else {
                    setNewClass({
                      ...newClass,
                      details: {
                        ...newClass.details,
                        materials: e.target.value
                          .split(",")
                          .map((m) => m.trim()),
                      },
                    });
                  }
                }}
                margin="normal"
                required
              />
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Main Image
                  {currentClass?.mainImage && (
                    <Chip
                      label="✓ Currently Live"
                      color="success"
                      size="small"
                      sx={{ ml: 2, fontWeight: 600 }}
                    />
                  )}
                </Typography>

                {/* Show existing main image first */}
                {currentClass?.mainImage &&
                  isValidImageUrl(currentClass.mainImage) && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        color="success.main"
                      >
                        ✓ Currently Displayed on Website:
                      </Typography>
                      <Box
                        sx={{
                          width: "100%",
                          height: 200,
                          borderRadius: 2,
                          overflow: "hidden",
                          border: "2px solid",
                          borderColor: "success.main",
                          position: "relative",
                        }}
                      >
                        <img
                          src={`${currentClass.mainImage}?t=${Date.now()}`}
                          alt="Main Image Preview"
                          loading="lazy"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            backgroundColor: "success.main",
                            color: "white",
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: "0.75rem",
                            fontWeight: "bold",
                          }}
                        >
                          LIVE
                        </Box>
                      </Box>
                    </Box>
                  )}

                {/* Show staged main image for new class (not yet saved) */}
                {!currentClass && newClass.mainImage && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      color="info.main"
                    >
                      📷 Staged Main Image (will be uploaded on save):
                    </Typography>
                    <Box
                      sx={{
                        width: "100%",
                        height: 200,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "2px dashed",
                        borderColor: "info.main",
                        position: "relative",
                      }}
                    >
                      <img
                        src={newClass.mainImage}
                        alt="Staged Main Image"
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          backgroundColor: "info.main",
                          color: "white",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                        }}
                      >
                        STAGED
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Upload section */}
                <Box sx={{ mt: 2 }}>
                  {!areRequiredFieldsFilled() && (
                    <Typography
                      variant="body2"
                      color="warning.main"
                      sx={{ mb: 2, fontStyle: "italic" }}
                    >
                      ⚠️ Please fill in all required fields above before uploading images.
                    </Typography>
                  )}
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    color="text.secondary"
                  >
                    {(currentClass?.mainImage || newClass.mainImage)
                      ? "Upload new image to replace current one:"
                      : "Upload main image:"}
                  </Typography>
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="main-image-upload"
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setImageUploading((prev) => ({ ...prev, main: true }));
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          const imageData = reader.result as string;
                          // Show crop dialog instead of directly setting image
                          setCropImageType("main");
                          setTempImageForCrop(imageData);
                          setCropDialogOpen(true);
                          setImageUploading((prev) => ({
                            ...prev,
                            main: false,
                          }));
                        };
                        reader.onerror = () => {
                          setImageUploading((prev) => ({
                            ...prev,
                            main: false,
                          }));
                        };
                        reader.readAsDataURL(file);
                      }
                      // Reset the input so the same file can be selected again
                      e.target.value = "";
                    }}
                  />
                  <label htmlFor="main-image-upload">
                    <Button
                      variant={
                        (currentClass?.mainImage || newClass.mainImage) ? "outlined" : "contained"
                      }
                      component="span"
                      fullWidth
                      disabled={imageUploading.main || submitting || !areRequiredFieldsFilled()}
                      startIcon={
                        imageUploading.main ? (
                          <CircularProgress size={20} />
                        ) : (currentClass?.mainImage || newClass.mainImage) ? (
                          <EditIcon />
                        ) : (
                          <UploadIcon />
                        )
                      }
                    >
                      {imageUploading.main
                        ? "Uploading..."
                        : (currentClass?.mainImage || newClass.mainImage)
                        ? "Replace Main Image"
                        : "Upload Main Image"}
                    </Button>
                  </label>
                </Box>

                {!(currentClass?.mainImage || newClass.mainImage) && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: "grey.50",
                      borderRadius: 2,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No main image currently displayed on website
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Thumbnail Image Section */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Thumbnail Image (for listing page)
                  {(currentClass?.thumbnailImage || newClass.thumbnailImage) && (
                    <Chip
                      label="✓ Set"
                      color="success"
                      size="small"
                      sx={{ ml: 2, fontWeight: 600 }}
                    />
                  )}
                </Typography>

                {/* Show existing thumbnail */}
                {((currentClass && currentClass.thumbnailImage) || (!currentClass && newClass.thumbnailImage)) && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      color={currentClass ? "success.main" : "info.main"}
                    >
                      {currentClass ? "✓ Currently Displayed:" : "📷 Staged:"}
                    </Typography>
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: 300,
                        height: 180,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "2px solid",
                        borderColor: currentClass ? "success.main" : "info.main",
                        position: "relative",
                      }}
                    >
                      <img
                        src={currentClass?.thumbnailImage || newClass.thumbnailImage}
                        alt="Thumbnail Preview"
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          backgroundColor: currentClass ? "success.main" : "info.main",
                          color: "white",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                        }}
                      >
                        {currentClass ? "LIVE" : "STAGED"}
                      </Box>
                    </Box>
                  </Box>
                )}

                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    color="text.secondary"
                  >
                    {(currentClass?.thumbnailImage || newClass.thumbnailImage)
                      ? "Upload new thumbnail to replace:"
                      : "Upload thumbnail (displays in class listing):"}
                  </Typography>
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="thumbnail-image-upload"
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setImageUploading((prev) => ({ ...prev, main: true }));
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          const imageData = reader.result as string;
                          setCropImageType("thumbnail");
                          setTempImageForCrop(imageData);
                          setCropDialogOpen(true);
                          setImageUploading((prev) => ({
                            ...prev,
                            main: false,
                          }));
                        };
                        reader.onerror = () => {
                          setImageUploading((prev) => ({
                            ...prev,
                            main: false,
                          }));
                        };
                        reader.readAsDataURL(file);
                      }
                      e.target.value = "";
                    }}
                  />
                  <label htmlFor="thumbnail-image-upload">
                    <Button
                      variant={(currentClass?.thumbnailImage || newClass.thumbnailImage) ? "outlined" : "contained"}
                      component="span"
                      fullWidth
                      disabled={imageUploading.main || submitting || !areRequiredFieldsFilled()}
                      startIcon={
                        imageUploading.main ? (
                          <CircularProgress size={20} />
                        ) : (currentClass?.thumbnailImage || newClass.thumbnailImage) ? (
                          <EditIcon />
                        ) : (
                          <UploadIcon />
                        )
                      }
                    >
                      {imageUploading.main
                        ? "Uploading..."
                        : (currentClass?.thumbnailImage || newClass.thumbnailImage)
                        ? "Replace Thumbnail"
                        : "Upload Thumbnail"}
                    </Button>
                  </label>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    Recommended: 4:3 aspect ratio. This image appears in the class listing cards.
                  </Typography>
                </Box>

                {!(currentClass?.thumbnailImage || newClass.thumbnailImage) && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: "grey.50",
                      borderRadius: 2,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No thumbnail set - will use main image as fallback
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Showcase Images
                  {currentClass?.gallery?.showcase &&
                    currentClass.gallery.showcase.filter((image) =>
                      isValidImageUrl(image)
                    ).length > 0 && (
                      <Chip
                        label={`✓ ${
                          currentClass.gallery.showcase.filter((image) =>
                            isValidImageUrl(image)
                          ).length
                        } Currently Live`}
                        color="success"
                        size="small"
                        sx={{ ml: 2, fontWeight: 600 }}
                      />
                    )}
                </Typography>

                {/* Show existing showcase images with enhanced controls */}
                {currentClass?.gallery?.showcase &&
                  Array.isArray(currentClass.gallery.showcase) &&
                  currentClass.gallery.showcase.length > 0 && (
                    <EnhancedGalleryManager
                      images={currentClass.gallery.showcase}
                      title="Showcase"
                      type="showcase"
                      isLive={true}
                      selectedImages={selectedShowcaseImages}
                      onToggleSelection={(index) => toggleImageSelection(index, "showcase")}
                      onToggleSelectAll={() => toggleSelectAll("showcase")}
                      onDelete={(index) => handleImageDelete(index, "showcase")}
                      onBulkDelete={() => handleBulkDelete("showcase")}
                      onCrop={(index) => handleCropGalleryImage(index, "showcase")}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDrop={(index) => handleDrop(index, "showcase")}
                      isValidImageUrl={isValidImageUrl}
                    />
                  )}

                {/* Upload section */}
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    color="text.secondary"
                  >
                    {currentClass?.gallery.showcase &&
                    currentClass.gallery.showcase.length > 0
                      ? "Upload new images to add to existing ones:"
                      : "Upload showcase images:"}
                  </Typography>
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="showcase-upload"
                    type="file"
                    multiple
                    onChange={(e) => {
                      const files = e.target.files;
                      if (files) {
                        handleImageUpload(files, "showcase");
                      }
                    }}
                  />
                  <label htmlFor="showcase-upload">
                    <Button
                      variant={
                        currentClass?.gallery.showcase &&
                        currentClass.gallery.showcase.length > 0
                          ? "outlined"
                          : "contained"
                      }
                      component="span"
                      fullWidth
                      disabled={imageUploading.showcase || submitting || !areRequiredFieldsFilled()}
                      startIcon={
                        imageUploading.showcase ? (
                          <CircularProgress size={20} />
                        ) : currentClass?.gallery.showcase &&
                          currentClass.gallery.showcase.length > 0 ? (
                          <EditIcon />
                        ) : (
                          <UploadIcon />
                        )
                      }
                    >
                      {imageUploading.showcase
                        ? "Uploading..."
                        : currentClass?.gallery?.showcase &&
                          currentClass.gallery.showcase.filter((image) =>
                            isValidImageUrl(image)
                          ).length > 0
                        ? `Add More Showcase Images (${
                            currentClass.gallery.showcase.filter((image) =>
                              isValidImageUrl(image)
                            ).length
                          } live)`
                        : !currentClass && newClass.gallery.showcase.length > 0
                        ? `Add More Showcase Images (${newClass.gallery.showcase.length} staged)`
                        : "Upload Showcase Images"}
                    </Button>
                  </label>
                </Box>

                {/* Show staged showcase images for new class */}
                {!currentClass && newClass.gallery.showcase.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      color="info.main"
                    >
                      📷 Staged Showcase Images ({newClass.gallery.showcase.length} will be uploaded on save):
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                        gap: 1,
                      }}
                    >
                      {newClass.gallery.showcase.map((image, index) => (
                        <Box
                          key={index}
                          sx={{
                            position: "relative",
                            borderRadius: 2,
                            overflow: "hidden",
                            border: "2px dashed",
                            borderColor: "info.main",
                          }}
                        >
                          <img
                            src={image}
                            alt={`Staged Showcase ${index + 1}`}
                            loading="lazy"
                            style={{
                              width: "100%",
                              height: 120,
                              objectFit: "cover",
                            }}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 4,
                              left: 4,
                              backgroundColor: "info.main",
                              color: "white",
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: "0.6rem",
                              fontWeight: "bold",
                            }}
                          >
                            STAGED
                          </Box>
                          <IconButton
                            onClick={() => handleImageDelete(index, "showcase")}
                            size="small"
                            sx={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              backgroundColor: "rgba(211, 47, 47, 0.9)",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "rgba(211, 47, 47, 1)",
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {(!currentClass?.gallery.showcase ||
                  currentClass.gallery.showcase.length === 0) &&
                  newClass.gallery.showcase.length === 0 && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: "grey.50",
                      borderRadius: 2,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No showcase images currently displayed on website
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Student Work Images
                  {currentClass?.gallery?.studentWork &&
                    currentClass.gallery.studentWork.filter((image) =>
                      isValidImageUrl(image)
                    ).length > 0 && (
                      <Chip
                        label={`✓ ${
                          currentClass.gallery.studentWork.filter((image) =>
                            isValidImageUrl(image)
                          ).length
                        } Currently Live`}
                        color="success"
                        size="small"
                        sx={{ ml: 2, fontWeight: 600 }}
                      />
                    )}
                </Typography>

                {/* Show existing student work images with enhanced controls */}
                {currentClass?.gallery?.studentWork &&
                  Array.isArray(currentClass.gallery.studentWork) &&
                  currentClass.gallery.studentWork.length > 0 && (
                    <EnhancedGalleryManager
                      images={currentClass.gallery.studentWork}
                      title="Student Work"
                      type="studentWork"
                      isLive={true}
                      selectedImages={selectedStudentWorkImages}
                      onToggleSelection={(index) => toggleImageSelection(index, "studentWork")}
                      onToggleSelectAll={() => toggleSelectAll("studentWork")}
                      onDelete={(index) => handleImageDelete(index, "studentWork")}
                      onBulkDelete={() => handleBulkDelete("studentWork")}
                      onCrop={(index) => handleCropGalleryImage(index, "studentWork")}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDrop={(index) => handleDrop(index, "studentWork")}
                      isValidImageUrl={isValidImageUrl}
                    />
                  )}

                {/* Upload section */}
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    color="text.secondary"
                  >
                    {currentClass?.gallery.studentWork &&
                    currentClass.gallery.studentWork.length > 0
                      ? "Upload new images to add to existing ones:"
                      : "Upload student work images:"}
                  </Typography>
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="student-work-upload"
                    type="file"
                    multiple
                    onChange={(e) => {
                      const files = e.target.files;
                      if (files) {
                        handleImageUpload(files, "studentWork");
                      }
                    }}
                  />
                  <label htmlFor="student-work-upload">
                    <Button
                      variant={
                        currentClass?.gallery.studentWork &&
                        currentClass.gallery.studentWork.length > 0
                          ? "outlined"
                          : "contained"
                      }
                      component="span"
                      fullWidth
                      disabled={imageUploading.studentWork || submitting || !areRequiredFieldsFilled()}
                      startIcon={
                        imageUploading.studentWork ? (
                          <CircularProgress size={20} />
                        ) : currentClass?.gallery.studentWork &&
                          currentClass.gallery.studentWork.length > 0 ? (
                          <EditIcon />
                        ) : (
                          <UploadIcon />
                        )
                      }
                    >
                      {imageUploading.studentWork
                        ? "Uploading..."
                        : currentClass?.gallery?.studentWork &&
                          currentClass.gallery.studentWork.filter((image) =>
                            isValidImageUrl(image)
                          ).length > 0
                        ? `Add More Student Work Images (${
                            currentClass.gallery.studentWork.filter((image) =>
                              isValidImageUrl(image)
                            ).length
                          } live)`
                        : !currentClass && newClass.gallery.studentWork.length > 0
                        ? `Add More Student Work Images (${newClass.gallery.studentWork.length} staged)`
                        : "Upload Student Work Images"}
                    </Button>
                  </label>
                </Box>

                {/* Show staged student work images for new class */}
                {!currentClass && newClass.gallery.studentWork.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      color="info.main"
                    >
                      📷 Staged Student Work Images ({newClass.gallery.studentWork.length} will be uploaded on save):
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                        gap: 1,
                      }}
                    >
                      {newClass.gallery.studentWork.map((image, index) => (
                        <Box
                          key={index}
                          sx={{
                            position: "relative",
                            borderRadius: 2,
                            overflow: "hidden",
                            border: "2px dashed",
                            borderColor: "info.main",
                          }}
                        >
                          <img
                            src={image}
                            alt={`Staged Student Work ${index + 1}`}
                            loading="lazy"
                            style={{
                              width: "100%",
                              height: 120,
                              objectFit: "cover",
                            }}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 4,
                              left: 4,
                              backgroundColor: "info.main",
                              color: "white",
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: "0.6rem",
                              fontWeight: "bold",
                            }}
                          >
                            STAGED
                          </Box>
                          <IconButton
                            onClick={() => handleImageDelete(index, "studentWork")}
                            size="small"
                            sx={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              backgroundColor: "rgba(211, 47, 47, 0.9)",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "rgba(211, 47, 47, 1)",
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {(!currentClass?.gallery.studentWork ||
                  currentClass.gallery.studentWork.length === 0) &&
                  newClass.gallery.studentWork.length === 0 && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: "grey.50",
                      borderRadius: 2,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No student work images currently displayed on website
                    </Typography>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={
                  submitting ||
                  Object.values(imageUploading).some((loading) => loading)
                }
                startIcon={
                  submitting ? <CircularProgress size={20} /> : undefined
                }
              >
                {submitting
                  ? "Saving..."
                  : currentClass
                  ? "Save Changes"
                  : "Add Class"}
              </Button>
            </DialogActions>
        </form>
        )}
      </Dialog>

      {/* Image Crop Dialog */}
      <ImageCropDialog
        open={cropDialogOpen}
        imageUrl={tempImageForCrop}
        onClose={() => {
          setCropDialogOpen(false);
          setTempImageForCrop("");
        }}
        onCropComplete={handleCroppedImage}
        aspectRatio={
          cropImageType === "main"
            ? 16 / 9
            : cropImageType === "thumbnail"
            ? 4 / 3
            : 1
        }
        title={
          cropImageType === "main"
            ? "Crop Main Image (16:9)"
            : cropImageType === "thumbnail"
            ? "Crop Thumbnail (4:3)"
            : `Crop ${cropGalleryType === "showcase" ? "Showcase" : "Student Work"} Image`
        }
      />
    </Container>
    </Box>
  );
};

export default Classes;
